import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authFetch } from '../utils/authFetch';

/*
  Console seriale di un dispositivo, lato admin.

  Non e' un collegamento diretto all'ESP32: mostra le righe che il firmware
  spedisce nel campo log_tail della diagnostica periodica (POST /api/iot/logs).
  Il ritardo e' quindi quello dell'intervallo di diagnostica del firmware, non
  quello del monitor seriale dell'IDE.

  Se il firmware installato non manda ancora log_tail la risposta arriva senza
  righe: in quel caso lo diciamo, invece di mostrare un riquadro vuoto che
  sembra un guasto.
*/

const REFRESH_INTERVAL_MS = 30000;
const DEFAULT_BATCH_LIMIT = 40;

// Le righe del firmware sono taggate [TAG]: coloro solo i casi che contano,
// il resto resta neutro.
const lineTone = (text = '') => {
  const upper = String(text).toUpperCase();

  if (/\b(ERROR|ERRORE|FAIL|FALLIT|PANIC|BROWNOUT)\b/.test(upper)) {
    return 'text-red-300';
  }

  if (/\b(WARN|ATTENZIONE|TIMEOUT|RETRY|SALTATO)\b/.test(upper)) {
    return 'text-amber-300';
  }

  if (/\b(OK|CONNESS|SUCCESS|SALVAT)/.test(upper)) {
    return 'text-emerald-300';
  }

  return 'text-gray-300';
};

const formatClock = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDateTime = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('it-IT');
};

export default function DeviceSerialConsole({ deviceId }) {
  const [lines, setLines] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [follow, setFollow] = useState(true);
  const [filter, setFilter] = useState('');

  const scrollRef = useRef(null);

  const fetchConsole = useCallback(async () => {
    if (!deviceId) return;

    try {
      setLoading(true);
      setError('');

      const res = await authFetch(
        `/api/iot/devices/${encodeURIComponent(deviceId)}/console?limit=${DEFAULT_BATCH_LIMIT}`
      );

      // authFetch restituisce null quando la sessione e' scaduta e ha gia'
      // fatto il redirect: qui non c'e' altro da fare.
      if (!res) return;

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.message || 'Errore nel recupero della console');
      }

      setLines(Array.isArray(data?.lines) ? data.lines : []);
      setMeta({
        batches: data?.batches || 0,
        lastReceivedAt: data?.last_received_at || null,
        lastTimestampUtc: data?.last_timestamp_utc || '',
        lastHealth: data?.last_health || null,
        logTailSupported: data?.log_tail_supported !== false,
        truncated: Boolean(data?.truncated),
      });
    } catch (err) {
      console.error('Errore fetch console seriale:', err);
      setError(err.message || 'Errore nel recupero della console');
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchConsole();
  }, [fetchConsole]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const timer = setInterval(fetchConsole, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchConsole]);

  // Memoizzato: senza, l'array sarebbe nuovo a ogni render e l'effetto di
  // scroll scatterebbe anche quando non e' cambiata nessuna riga.
  const visibleLines = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return lines;

    return lines.filter((line) =>
      String(line?.text || '')
        .toLowerCase()
        .includes(needle)
    );
  }, [lines, filter]);

  useEffect(() => {
    if (!follow || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [visibleLines, follow]);

  const handleDownload = () => {
    // Si scarica quello che si sta guardando: con un filtro attivo, scaricare
    // tutto il resto sarebbe una sorpresa.
    const content = visibleLines
      .map((line) => {
        const clock = formatClock(line?.received_at);
        return clock ? `[${clock}] ${line?.text || ''}` : String(line?.text || '');
      })
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `console_${deviceId}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.log`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-cyan-700">
            Console seriale
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Righe inviate dal dispositivo <strong>{deviceId}</strong> con la diagnostica
            periodica. Non e' un collegamento diretto: l'ultimo aggiornamento risale a{' '}
            {formatDateTime(meta?.lastReceivedAt)}.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtra righe"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-400"
          />

          <button
            type="button"
            onClick={fetchConsole}
            disabled={loading}
            className="px-3 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-medium transition disabled:opacity-60"
          >
            {loading ? 'Aggiorno...' : 'Aggiorna'}
          </button>

          <button
            type="button"
            onClick={handleDownload}
            disabled={visibleLines.length === 0}
            className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition disabled:opacity-60"
          >
            Scarica .log
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Aggiornamento automatico (30 s)
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={follow}
            onChange={(e) => setFollow(e.target.checked)}
          />
          Segui ultima riga
        </label>

        <span>
          Diagnostiche lette: <strong>{meta?.batches ?? 0}</strong>
        </span>

        <span>
          Righe: <strong>{visibleLines.length}</strong>
          {filter.trim() && lines.length !== visibleLines.length
            ? ` su ${lines.length}`
            : ''}
        </span>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {meta && meta.batches > 0 && !meta.logTailSupported && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Il dispositivo manda la diagnostica ma nessuna riga di seriale. Quasi sempre
          vuol dire che il firmware installato e' precedente a quello che compila{' '}
          <code>log_tail</code> nel payload: in quel caso la console resta vuota finche'
          non viene aggiornato.
        </div>
      )}

      {meta?.truncated && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
          Sono mostrate solo le righe piu' recenti: il dispositivo ne ha inviate piu' di
          quante la console ne tenga a video.
        </div>
      )}

      <div
        ref={scrollRef}
        className="h-[420px] overflow-auto rounded-xl bg-gray-900 p-4 font-mono text-xs leading-5"
      >
        {visibleLines.length === 0 ? (
          <p className="text-gray-500">
            {loading ? 'Caricamento...' : 'Nessuna riga disponibile.'}
          </p>
        ) : (
          visibleLines.map((line, index) => {
            const clock = formatClock(line?.received_at);

            return (
              <div
                key={`${index}-${line?.text || ''}`}
                className={`whitespace-pre-wrap break-words ${lineTone(line?.text)}`}
              >
                {clock && <span className="text-gray-600 select-none">[{clock}] </span>}
                {line?.text}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
